import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scopeTenant, setScopeTenant] = useState(localStorage.getItem("ar_scope_tenant") || null);

  const loadMe = useCallback(async () => {
    const token = localStorage.getItem("ar_token");
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      setTenant(data.tenant);
    } catch {
      localStorage.removeItem("ar_token");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMe();
  }, [loadMe]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("ar_token", data.token);
      localStorage.setItem("ar_role", data.user.rol);
      setUser(data.user);
      setTenant(data.tenant);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatError(e.response?.data?.detail) };
    }
  };

  const register = async (payload) => {
    try {
      const { data } = await api.post("/registro", payload);
      localStorage.setItem("ar_token", data.token);
      localStorage.setItem("ar_role", data.user.rol);
      setUser(data.user);
      setTenant(data.tenant);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatError(e.response?.data?.detail) };
    }
  };

  const logout = () => {
    localStorage.removeItem("ar_token");
    localStorage.removeItem("ar_role");
    localStorage.removeItem("ar_scope_tenant");
    setUser(null);
    setTenant(null);
    setScopeTenant(null);
  };

  const selectScopeTenant = (id, nombre) => {
    if (id) {
      localStorage.setItem("ar_scope_tenant", id);
      localStorage.setItem("ar_scope_nombre", nombre || "");
    } else {
      localStorage.removeItem("ar_scope_tenant");
      localStorage.removeItem("ar_scope_nombre");
    }
    setScopeTenant(id);
  };

  return (
    <AuthContext.Provider
      value={{ user, tenant, loading, login, register, logout, scopeTenant, selectScopeTenant, reload: loadMe }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);

const NORMA_FALLBACK = {
  codigo: "IFS_V8", nombre: "IFS Food v8", nombre_corto: "IFS Food v8", etiqueta_critico: "KO",
};

// Etiqueta de presentación del tipo crítico ("KO" en IFS, "CRÍTICO LEGAL" en Sanidad).
export const useEtiquetaCritico = () => {
  const { tenant } = useAuth();
  return tenant?.norma_config?.etiqueta_critico || NORMA_FALLBACK.etiqueta_critico;
};

// Configuración completa de la norma activa del tenant.
export const useNorma = () => {
  const { tenant } = useAuth();
  return tenant?.norma_config || NORMA_FALLBACK;
};

// Nombres de los 4 niveles según la norma activa. N1-N3 son los planes (fijos);
// N4 usa la etiqueta de la norma (CERTIFICA en IFS, VERIFICA en Sanidad).
export const useNivelLabels = () => {
  const norma = useNorma();
  return ["DOCUMENTA", "REGISTRA", "TRAZA", norma?.etiqueta_nivel_4 || "CERTIFICA"];
};

// Overline "Nivel 4 · Certifica" / "Nivel 4 · Verifica" según norma activa.
export const useNivel4Overline = () => {
  const l = useNivelLabels()[3] || "CERTIFICA";
  return `Nivel 4 · ${l.charAt(0)}${l.slice(1).toLowerCase()}`;
};
